import { Card, Col, Row, Empty, Typography, Tag, Select, Space } from "antd";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import {
  BookOutlined,
  LinkOutlined,
  CalendarOutlined,
} from "@ant-design/icons";

const { Text } = Typography;
const { Option } = Select;

const PublicationsTab = ({ institution }) => {
  const { publications = [] } = institution;

  const [selectedYear, setSelectedYear] = useState("All");
  const [selectedAuthor, setSelectedAuthor] = useState("All");
  const [selectedPublicationType, setSelectedPublicationType] = useState("All");

  // Extract unique years as strings
  const publicationYears = useMemo(() => {
    const years = publications
      .map((pub) => {
        const year = new Date(pub.date_published).getFullYear();
        return isNaN(year) ? null : year.toString();
      })
      .filter((year) => year !== null);
    return ["All", ...Array.from(new Set(years)).sort((a, b) => b - a)];
  }, [publications]);

  // Extract unique authors
  const allAuthors = useMemo(() => {
    const authors = publications.flatMap((pub) =>
      pub.authors ? pub.authors.split(",").map((a) => a.trim()) : []
    );
    return ["All", ...Array.from(new Set(authors))];
  }, [publications]);

  // Extract unique publication types
  const publicationTypes = useMemo(() => {
    const types = publications
      .map((pub) => pub.publication_type?.name)
      .filter((type) => type && type.trim() !== "");
    return ["All", ...Array.from(new Set(types))];
  }, [publications]);

  // Filtered publications
  const filtered = useMemo(() => {
    return publications.filter((pub) => {
      const matchesYear =
        selectedYear === "All" ||
        new Date(pub.date_published).getFullYear().toString() === selectedYear;

      const matchesAuthor =
        selectedAuthor === "All" ||
        (pub.authors &&
          pub.authors.toLowerCase().includes(selectedAuthor.toLowerCase()));

      const matchesType =
        selectedPublicationType === "All" ||
        pub.publication_type?.name === selectedPublicationType;

      return matchesYear && matchesAuthor && matchesType;
    });
  }, [publications, selectedYear, selectedAuthor, selectedPublicationType]);

  return (
    <div className="pt-5 pb-6">
      <Space className="mb-6 flex-wrap" size="large">
        {/* Year Filter */}
        <Select
          value={selectedYear}
          onChange={setSelectedYear}
          style={{ minWidth: 160 }}
          placeholder="Filter by Year"
        >
          {publicationYears.map((year) => (
            <Option key={year} value={year}>
              {year}
            </Option>
          ))}
        </Select>

        {/* Author Filter */}
        <Select
          value={selectedAuthor}
          onChange={setSelectedAuthor}
          style={{ minWidth: 200 }}
          showSearch
          filterOption={(input, option) =>
            option?.children?.toLowerCase().includes(input.toLowerCase())
          }
          placeholder="Filter by Author"
        >
          {allAuthors.map((author) => (
            <Option key={author} value={author}>
              {author}
            </Option>
          ))}
        </Select>

        {/* Publication Type Filter */}
        <Select
          value={selectedPublicationType}
          onChange={setSelectedPublicationType}
          style={{ minWidth: 220 }}
          placeholder="Filter by Publication Type"
        >
          {publicationTypes.map((type) => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </Select>
      </Space>

      {/* Publications */}
      {filtered.length === 0 ? (
        <Empty
          description="No publications found for selected filters."
          className="my-12"
        />
      ) : (
        <Row gutter={[24, 24]}>
          {filtered.map((pub) => (
            <Col key={pub.id} xs={24} sm={12} md={6}>
              <Card
                title={
                  <div className="text-base font-semibold text-gray-800 flex items-center gap-2">
                    <BookOutlined className="text-blue-600" />
                    {pub.title}
                  </div>
                }
                className="border-gray-200! hover:shadow transition-all border"
                extra={
                  pub.uri && (
                    <a
                      href={pub.uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm"
                    >
                      <LinkOutlined /> View
                    </a>
                  )
                }
              >
                <div className="text-sm text-gray-700 space-y-2">
                  <p>
                    <strong>Authors:</strong>{" "}
                    {pub.authors || <span className="text-gray-400">—</span>}
                  </p>

                  {pub.publication_type?.name && (
                    <p>
                      <strong>Type:</strong>{" "}
                      <Tag color="blue">{pub.publication_type.name}</Tag>
                    </p>
                  )}

                  {pub.date_published && (
                    <p>
                      <CalendarOutlined className="mr-1 text-gray-500" />
                      <strong>Published:</strong>{" "}
                      <Tag color="green">
                        {format(new Date(pub.date_published), "do MMM yyyy")}
                      </Tag>
                    </p>
                  )}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default PublicationsTab;
